import {GROUP_PATH_SEPARATOR} from "@/helpers/catalogWorkbookData";
import {normalizeName} from "@/helpers/import/matchNames";
import {GROUP, USE_PREFIX} from "@/helpers/import/buildImportReferences";
import {vatLabelToPercent} from "@/helpers/vat";

export const REUSE = 'reuse';
export const CREATE_GROUP = 'create';

const GROUP_SETTINGS = [
    'vatIn',
    'vatOut',
    'color',
    'orderIndex',
    'userHidden',
    'webHidden',
    'kioskHidden',
    'imageUrl',
    'externalReference',
    'maxChoiceFree',
    'maxChoicePaid',
    'forceChoiceAmountFree',
    'forceChoiceAmountPaid',
];

function validRows(sheets, name) {
    return (sheets[name]?.rows || []).filter(row => !row.excluded);
}

function ancestorsAndSelf(path) {
    const parts = path.split(GROUP_PATH_SEPARATOR);
    return parts.map((_, index) => parts.slice(0, index + 1).join(GROUP_PATH_SEPARATOR));
}

function createGroupResolver({sheets, orgPaths, choices}) {
    const canonical = new Map();
    [...orgPaths, ...validRows(sheets, 'Groups').map(row => row.values.path)]
        .filter(Boolean)
        .forEach(path => {
            if (!canonical.has(normalizeName(path))) canonical.set(normalizeName(path), path);
        });

    return path => {
        if (!path) return '';
        const parts = path.split(GROUP_PATH_SEPARATOR);
        let resolved = '';
        parts.forEach((part, index) => {
            const original = parts.slice(0, index + 1).join(GROUP_PATH_SEPARATOR);
            const choice = choices.get(`${GROUP}:${normalizeName(original)}`);
            let current = choice?.startsWith(USE_PREFIX)
                ? choice.slice(USE_PREFIX.length)
                : (resolved ? `${resolved}${GROUP_PATH_SEPARATOR}${part}` : part);
            current = canonical.get(normalizeName(current)) || current;
            resolved = current;
        });
        return resolved;
    };
}

function referencedPaths(sheets, resolve) {
    const paths = [];
    validRows(sheets, 'Groups').forEach(row => paths.push(resolve(row.values.path)));
    validRows(sheets, 'Products').forEach(row => {
        paths.push(resolve(row.values.group));
        [...(row.values.freeOptionGroups || []), ...(row.values.paidOptionGroups || [])].forEach(path => paths.push(resolve(path)));
    });
    validRows(sheets, 'OptionListItems').forEach(row => {
        if (row.values.productGroup) paths.push(resolve(row.values.productGroup));
    });
    return paths.filter(Boolean);
}

export function buildImportGroups({sheets, orgGroups, orgPaths, choices}) {
    const resolve = createGroupResolver({sheets, orgPaths, choices});
    const orgByPath = new Map(orgGroups.map((group, index) => [normalizeName(orgPaths[index]), group]));

    const settingsByPath = new Map();
    validRows(sheets, 'Groups').forEach(row => {
        const key = normalizeName(resolve(row.values.path));
        if (!settingsByPath.has(key)) settingsByPath.set(key, row.values);
    });

    const productsByPath = new Map();
    validRows(sheets, 'Products').forEach(row => {
        const key = normalizeName(resolve(row.values.group));
        productsByPath.set(key, [...(productsByPath.get(key) || []), row.values]);
    });

    const groups = new Map();
    referencedPaths(sheets, resolve).forEach(path => {
        ancestorsAndSelf(path).forEach(groupPath => {
            const key = normalizeName(groupPath);
            if (groups.has(key)) return;
            const parts = groupPath.split(GROUP_PATH_SEPARATOR);
            const existing = orgByPath.get(key);
            const settings = settingsByPath.get(key) || {};
            const firstProduct = (productsByPath.get(key) || []).find(product => product.vatIn !== null && product.vatIn !== undefined);
            const existingVat = side => existing?.[`vat${side}`] ?? vatLabelToPercent(existing?.[`vatLabel${side}`]);
            const vatIn = existing ? existingVat('In') : (settings.vatIn ?? firstProduct?.vatIn ?? null);
            groups.set(key, {
                key,
                path: groupPath,
                name: parts[parts.length - 1],
                depth: parts.length - 1,
                parentKey: parts.length > 1 ? normalizeName(parts.slice(0, -1).join(GROUP_PATH_SEPARATOR)) : null,
                status: existing ? REUSE : CREATE_GROUP,
                productGroupId: existing?.productGroupId ?? null,
                productCount: (productsByPath.get(key) || []).length,
                settings: Object.fromEntries(GROUP_SETTINGS.map(field => [field, settings[field] ?? null])),
                defaults: {
                    vatIn,
                    vatOut: existing ? existingVat('Out') : (settings.vatOut ?? firstProduct?.vatOut ?? vatIn),
                    color: settings.color ?? null,
                },
            });
        });
    });

    const byParent = new Map();
    groups.forEach(group => byParent.set(group.parentKey, [...(byParent.get(group.parentKey) || []), group]));
    const ordered = [];
    const visit = parentKey => {
        (byParent.get(parentKey) || [])
            .sort((a, b) => (a.settings.orderIndex ?? Infinity) - (b.settings.orderIndex ?? Infinity) || a.name.localeCompare(b.name))
            .forEach(group => {
                ordered.push(group);
                visit(group.key);
            });
    };
    visit(null);
    return {groups: ordered, resolve};
}
